import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import { StoreContext } from "components/StoreContext";
import Loading from "components/Loading";
import image from "components/Images/DotGridSm.svg";
import FeaturedProduct from "./FeaturedProduct";

const FeaturedProducts = ({ title, endpoint }) => {
  const { store, StoreAPI } = useContext(StoreContext);
  const [slides, setSlides] = useState();
  const [headline, setHeadline] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState(null);

  const Carousel = () => (
    <div className="featuredProductHolder">
      <div className="featuredProductHolder__content">
        <CarouselProvider
          naturalSlideWidth={100}
          naturalSlideHeight={100}
          totalSlides={products.length}
          visibleSlides={slides}
        >
          <div className="featuredProduct__header">
            <div className="featuredProduct__title">
              <img src={image} alt="Dot Grid" />
              {headline && <h3>{headline}</h3>}
              {!headline && title && <h3>{title}</h3>}
            </div>
            <div className="featuredProduct__buttons">
              <ButtonBack aria-label="Button Back">
                <FontAwesomeIcon icon={faChevronLeft} />
              </ButtonBack>
              <ButtonNext aria-label="Button next">
                <FontAwesomeIcon icon={faChevronRight} />
              </ButtonNext>
            </div>
          </div>
          <Slider>
            {products.map((product, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Slide key={product.sku + index}>
                <FeaturedProduct
                  info={product}
                  ce={product.ceCredits > 0}
                  sku={product.sku}
                  featImg={
                    product.imageSmallThumbnailPath
                      ? product.imageSmallThumbnailPath
                      : product.image
                  }
                  logo={product.partnerImage}
                  partner={product.partnerShortName}
                  title={product.productName}
                  bundleCount={product.courseBundleCount}
                  shortDescription={product.shortDescription}
                  cost={product.retailPrice}
                  discountPrice={product.salePrice ? product.salePrice : null}
                />
              </Slide>
            ))}
          </Slider>
        </CarouselProvider>
      </div>
    </div>
  );

  const handleResize = () => {
    const width = window.innerWidth;
    if (width >= 1080) {
      setSlides(4);
    } else if (width < 1080 && width >= 810) {
      setSlides(3);
    } else if (width < 810 && width >= 530) {
      setSlides(2);
    } else {
      setSlides(1.25);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    if (typeof Event === "function") {
      // modern browsers
      window.dispatchEvent(new Event("resize"));
    } else {
      // for IE and other old browsers
      // causes deprecation warning on modern browsers
      const evt = window.document.createEvent("UIEvents");
      evt.initUIEvent("resize", true, false, window, 0);
      window.dispatchEvent(evt);
    }
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (store.loaded) {
      setLoading(true);
      setProducts(null);
      StoreAPI.get(`products/${endpoint}`)
        .then(response => {
          setProducts(response.data.products);
          setHeadline(response.data.headLine);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.loaded, endpoint]);

  if (loading) {
    return (
      <div className="featuredProductBlock">
        <div className="featuredProductHolder">
          <div className="featuredProductHolder__content">
            <Loading bg="transparent" />
          </div>
        </div>
      </div>
    );
  }

  if (!loading && products && products.length >= 4) {
    return (
      <div className="featuredProductBlock">
        {products.length >= 4 && <Carousel />}
      </div>
    );
  }

  return null;
};

FeaturedProducts.propTypes = {
  title: PropTypes.string,
  endpoint: PropTypes.string.isRequired
};

FeaturedProducts.defaultProps = {
  title: "Featured Products"
};

export default FeaturedProducts;
